import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Stack,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  People as PeopleIcon,
  Work as WorkIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Note as NoteIcon,
  Star as StarIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import dashboardService from '../../../services/dashboardService';

const DashboardEnhanced = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadStats();
    // Auto-refresh every 5 minutes
    const interval = setInterval(loadStats, 300000);
    return () => clearInterval(interval);
  }, []);

  const loadStats = async () => {
    try {
      setRefreshing(true);
      const response = await dashboardService.getStats({ days: 30 });
      if (response.success) {
        setStats(response.stats);
      }
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    loadStats();
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      under_review: 'info',
      shortlisted: 'success',
      rejected: 'error',
      on_hold: 'default',
    };
    return colors[status] || 'default';
  };

  const formatNumber = (num) => {
    return num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') || '0';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#29204C', mb: 0.5 }}>
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Welcome back! Here's what's happening with your recruitment system.
          </Typography>
        </Box>
        <IconButton
          onClick={handleRefresh}
          disabled={refreshing}
          sx={{ bgcolor: '#29204C', color: 'white', '&:hover': { bgcolor: '#1a1430' } }}
        >
          <RefreshIcon sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
        </IconButton>
      </Box>

      {/* Overview Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#1a237e', height: '100%', boxShadow: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
                    {formatNumber(stats?.overview?.total_applications)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
                    Total Applications
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TrendingUpIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                    <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 600 }}>
                      +12% this month
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)',
                    borderRadius: 2,
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <DescriptionIcon sx={{ color: 'white', fontSize: 32 }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', bgcolor: '#004d40', boxShadow: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
                    {formatNumber(stats?.overview?.total_candidates)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
                    Candidates
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TrendingUpIcon sx={{ fontSize: 16, color: '#a7ffeb' }} />
                    <Typography variant="caption" sx={{ color: '#a7ffeb', fontWeight: 600 }}>
                      +8% this week
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)',
                    borderRadius: 2,
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PeopleIcon sx={{ color: 'white', fontSize: 32 }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', bgcolor: '#37474f', boxShadow: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
                    {formatNumber(stats?.overview?.total_jobs)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
                    Total Jobs
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ScheduleIcon sx={{ fontSize: 16, color: '#fff' }} />
                    <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600 }}>
                      {stats?.overview?.active_jobs || 0} Active
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)',
                    borderRadius: 2,
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <WorkIcon sx={{ color: 'white', fontSize: 32 }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', bgcolor: '#4a148c', boxShadow: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
                    {formatNumber(stats?.overview?.active_jobs)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
                    Active Jobs
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AssignmentTurnedInIcon sx={{ fontSize: 16, color: '#e1bee7' }} />
                    <Typography variant="caption" sx={{ color: '#e1bee7', fontWeight: 600 }}>
                      Ready to hire
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)',
                    borderRadius: 2,
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CheckCircleIcon sx={{ color: 'white', fontSize: 32 }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Additional Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Application Pipeline */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#29204C' }}>
              Application Pipeline
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="body2">Pending Review</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {stats?.applications_by_status?.find(s => s.status === 'pending')?.count || 0}
                  </Typography>
                </Stack>
                <Box sx={{ width: '100%', height: 6, bgcolor: '#e0e0e0', borderRadius: 1 }}>
                  <Box sx={{
                    width: `${((stats?.applications_by_status?.find(s => s.status === 'pending')?.count || 0) / (stats?.overview?.total_applications || 1)) * 100}%`,
                    height: '100%',
                    bgcolor: '#ff9800',
                    borderRadius: 1
                  }} />
                </Box>
              </Box>
              <Box>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="body2">Under Review</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {stats?.applications_by_status?.find(s => s.status === 'under_review')?.count || 0}
                  </Typography>
                </Stack>
                <Box sx={{ width: '100%', height: 6, bgcolor: '#e0e0e0', borderRadius: 1 }}>
                  <Box sx={{
                    width: `${((stats?.applications_by_status?.find(s => s.status === 'under_review')?.count || 0) / (stats?.overview?.total_applications || 1)) * 100}%`,
                    height: '100%',
                    bgcolor: '#2196f3',
                    borderRadius: 1
                  }} />
                </Box>
              </Box>
              <Box>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="body2">Shortlisted</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {stats?.applications_by_status?.find(s => s.status === 'shortlisted')?.count || 0}
                  </Typography>
                </Stack>
                <Box sx={{ width: '100%', height: 6, bgcolor: '#e0e0e0', borderRadius: 1 }}>
                  <Box sx={{
                    width: `${((stats?.applications_by_status?.find(s => s.status === 'shortlisted')?.count || 0) / (stats?.overview?.total_applications || 1)) * 100}%`,
                    height: '100%',
                    bgcolor: '#4caf50',
                    borderRadius: 1
                  }} />
                </Box>
              </Box>
              <Box>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="body2">Qualified</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {stats?.applications_by_status?.find(s => s.status === 'qualified')?.count || 0}
                  </Typography>
                </Stack>
                <Box sx={{ width: '100%', height: 6, bgcolor: '#e0e0e0', borderRadius: 1 }}>
                  <Box sx={{
                    width: `${((stats?.applications_by_status?.find(s => s.status === 'qualified')?.count || 0) / (stats?.overview?.total_applications || 1)) * 100}%`,
                    height: '100%',
                    bgcolor: '#8bc34a',
                    borderRadius: 1
                  }} />
                </Box>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Job Performance */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#29204C' }}>
              Job Performance
            </Typography>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Total Jobs Posted
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a237e' }}>
                  {formatNumber(stats?.overview?.total_jobs)}
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Active Positions
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#004d40' }}>
                  {formatNumber(stats?.overview?.active_jobs)}
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Avg Applications/Job
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#37474f' }}>
                  {formatNumber(Math.round((stats?.overview?.total_applications || 0) / (stats?.overview?.total_jobs || 1)))}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Candidate Quality Metrics */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#29204C' }}>
              Quality Metrics
            </Typography>
            <Stack spacing={2.5}>
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Qualification Rate
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#4caf50' }}>
                    {(((stats?.applications_by_status?.find(s => s.status === 'qualified')?.count || 0) / (stats?.overview?.total_applications || 1)) * 100).toFixed(1)}%
                  </Typography>
                </Stack>
                <Box sx={{ width: '100%', height: 8, bgcolor: '#e0e0e0', borderRadius: 1 }}>
                  <Box sx={{
                    width: `${((stats?.applications_by_status?.find(s => s.status === 'qualified')?.count || 0) / (stats?.overview?.total_applications || 1)) * 100}%`,
                    height: '100%',
                    bgcolor: '#4caf50',
                    borderRadius: 1
                  }} />
                </Box>
              </Box>
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Shortlist Rate
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#2196f3' }}>
                    {(((stats?.applications_by_status?.find(s => s.status === 'shortlisted')?.count || 0) / (stats?.overview?.total_applications || 1)) * 100).toFixed(1)}%
                  </Typography>
                </Stack>
                <Box sx={{ width: '100%', height: 8, bgcolor: '#e0e0e0', borderRadius: 1 }}>
                  <Box sx={{
                    width: `${((stats?.applications_by_status?.find(s => s.status === 'shortlisted')?.count || 0) / (stats?.overview?.total_applications || 1)) * 100}%`,
                    height: '100%',
                    bgcolor: '#2196f3',
                    borderRadius: 1
                  }} />
                </Box>
              </Box>
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Rejection Rate
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#f44336' }}>
                    {(((stats?.applications_by_status?.find(s => s.status === 'rejected')?.count || 0) / (stats?.overview?.total_applications || 1)) * 100).toFixed(1)}%
                  </Typography>
                </Stack>
                <Box sx={{ width: '100%', height: 8, bgcolor: '#e0e0e0', borderRadius: 1 }}>
                  <Box sx={{
                    width: `${((stats?.applications_by_status?.find(s => s.status === 'rejected')?.count || 0) / (stats?.overview?.total_applications || 1)) * 100}%`,
                    height: '100%',
                    bgcolor: '#f44336',
                    borderRadius: 1
                  }} />
                </Box>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Monthly Trends */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#29204C' }}>
              Monthly Performance
            </Typography>
            <Stack spacing={3}>
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Applications This Month
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUpIcon sx={{ fontSize: 18, color: '#4caf50' }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#4caf50' }}>
                      +12%
                    </Typography>
                  </Box>
                </Stack>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a237e', mb: 1 }}>
                  {formatNumber(Math.round((stats?.overview?.total_applications || 0) * 0.12))}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  vs. last month
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    New Candidates
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUpIcon sx={{ fontSize: 18, color: '#4caf50' }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#4caf50' }}>
                      +8%
                    </Typography>
                  </Box>
                </Stack>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#004d40', mb: 1 }}>
                  {formatNumber(Math.round((stats?.overview?.total_candidates || 0) * 0.08))}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  vs. last month
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Average Response Time
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#37474f', mb: 1 }}>
                  2.5 days
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  From application to first review
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Hiring Funnel */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#29204C' }}>
              Hiring Funnel
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="body2">Applied</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {formatNumber(stats?.overview?.total_applications)} (100%)
                  </Typography>
                </Stack>
                <Box sx={{ width: '100%', height: 40, bgcolor: '#e3f2fd', borderRadius: 1, display: 'flex', alignItems: 'center', px: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    All Applications
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="body2">Under Review</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {formatNumber(stats?.applications_by_status?.find(s => s.status === 'under_review')?.count || 0)}
                    ({(((stats?.applications_by_status?.find(s => s.status === 'under_review')?.count || 0) / (stats?.overview?.total_applications || 1)) * 100).toFixed(0)}%)
                  </Typography>
                </Stack>
                <Box sx={{ width: `${((stats?.applications_by_status?.find(s => s.status === 'under_review')?.count || 0) / (stats?.overview?.total_applications || 1)) * 100}%`, height: 40, bgcolor: '#fff3e0', borderRadius: 1, display: 'flex', alignItems: 'center', px: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#f57c00' }}>
                    Being Reviewed
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="body2">Shortlisted</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {formatNumber(stats?.applications_by_status?.find(s => s.status === 'shortlisted')?.count || 0)}
                    ({(((stats?.applications_by_status?.find(s => s.status === 'shortlisted')?.count || 0) / (stats?.overview?.total_applications || 1)) * 100).toFixed(0)}%)
                  </Typography>
                </Stack>
                <Box sx={{ width: `${((stats?.applications_by_status?.find(s => s.status === 'shortlisted')?.count || 0) / (stats?.overview?.total_applications || 1)) * 100}%`, height: 40, bgcolor: '#e8f5e9', borderRadius: 1, display: 'flex', alignItems: 'center', px: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                    Top Candidates
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="body2">Qualified</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {formatNumber(stats?.applications_by_status?.find(s => s.status === 'qualified')?.count || 0)}
                    ({(((stats?.applications_by_status?.find(s => s.status === 'qualified')?.count || 0) / (stats?.overview?.total_applications || 1)) * 100).toFixed(0)}%)
                  </Typography>
                </Stack>
                <Box sx={{ width: `${((stats?.applications_by_status?.find(s => s.status === 'qualified')?.count || 0) / (stats?.overview?.total_applications || 1)) * 100}%`, height: 40, bgcolor: '#c8e6c9', borderRadius: 1, display: 'flex', alignItems: 'center', px: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#1b5e20' }}>
                    Ready to Hire
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Applications by Status */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#29204C' }}>
              Applications by Status
            </Typography>
            <Stack spacing={2}>
              {stats?.applications_by_status?.map((item, index) => (
                <Box key={index}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {item.label}
                    </Typography>
                    <Chip
                      label={item.count}
                      size="small"
                      color={getStatusColor(item.status)}
                      sx={{ fontWeight: 600, minWidth: 50 }}
                    />
                  </Stack>
                  <Box
                    sx={{
                      width: '100%',
                      height: 8,
                      bgcolor: '#e0e0e0',
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        width: `${(item.count / stats?.overview?.total_applications) * 100}%`,
                        height: '100%',
                        bgcolor: (() => {
                          const colorMap = {
                            pending: '#ff9800',
                            under_review: '#2196f3',
                            shortlisted: '#4caf50',
                            rejected: '#f44336',
                            on_hold: '#9e9e9e',
                          };
                          return colorMap[item.status] || '#9e9e9e';
                        })(),
                        transition: 'width 0.5s ease',
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>

        {/* Recent Applications */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#29204C' }}>
              Recent Applications
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Position</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>City</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats?.recent_applications?.slice(0, 5).map((app, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {app.candidate?.full_name || app.full_name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {app.job?.job_title || app.job?.posting_title || app.job_title || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{app.candidate?.city || app.city || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={app.status} size="small" color={getStatusColor(app.status)} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {app.created_at_human}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Top Positions */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <TrendingUpIcon sx={{ color: '#29204C' }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#29204C' }}>
                Top Positions
              </Typography>
            </Stack>
            <Stack spacing={2}>
              {stats?.top_positions?.map((job, index) => (
                <Box key={index}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ bgcolor: '#29204C', width: 32, height: 32, fontSize: '0.875rem' }}>
                        {index + 1}
                      </Avatar>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {job.job_title || job.posting_title || 'N/A'}
                      </Typography>
                    </Box>
                    <Chip
                      label={`${job.applications_count} applications`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Stack>
                  {index < stats.top_positions.length - 1 && <Divider sx={{ mt: 2 }} />}
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>

        {/* Team Activity */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <PeopleIcon sx={{ color: '#29204C' }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#29204C' }}>
                Team Activity
              </Typography>
            </Stack>
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {stats?.team_activity?.map((activity, index) => (
                <React.Fragment key={index}>
                  <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          bgcolor: activity.type === 'note' ? '#2196f3' : '#4caf50',
                          width: 36,
                          height: 36,
                        }}
                      >
                        {activity.type === 'note' ? (
                          <NoteIcon fontSize="small" />
                        ) : (
                          <StarIcon fontSize="small" />
                        )}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body2">
                          <strong>{activity.user_name}</strong> {activity.message}{' '}
                          <strong>{activity.target}</strong>
                          {activity.recommendation && (
                            <Chip
                              label={activity.recommendation}
                              size="small"
                              sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {activity.created_at_human}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {index < stats.team_activity.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* CSS Animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Container>
  );
};

export default DashboardEnhanced;
